'use client';

import React, { useCallback, useEffect, useState } from 'react';
import useMediaQuery from '@/hooks/useMediaQuery';
import { mediumDesktop } from '@/lib/utils/screensizes';
import { useParams } from 'next/navigation';
import useProjectBuilder from '@/lib/hooks/useProjectBuilder';
import NavigationMenu from '@/components/organisms/project-builder/navigation-menu/navigation-menu';
import Spinner from '@/components/organisms/spinner';
import Button from '@/components/atoms/project-builder/button';
import ProjectListWrapper from '@/components/organisms/project-builder/list';
import Link from '@/components/atoms/link';
import Dashboard from '@/components/pages/dashboard';
import Typography from '@/components/atoms/typography';
import Modal from '@/components/organisms/modal';
import ProjectBuilderGuide from '@/components/organisms/project-builder-guide';


const ProjectListMemo = React.memo(ProjectListWrapper);

interface ProjectList {
  count: number;
  limit: number;
  offset: number;
  total: number;
  results: any;
}

interface ProjectCountData {
  count: number;
  limit: number;
  offset: number;
  total: number;
  results: any[];
}

type ProjectCountType = 'my' | 'open' | 'completed';

export type TotalCountState = Record<ProjectCountType, ProjectCountData>;

export default function ProjectDashboard() {
  const [isMediumDesktop] = useMediaQuery(mediumDesktop);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  
  const [loading, setLoading] = useState(true);
  const [refetch, setRefetch] = useState(true);
  const [totalCount, setTotalCount] = useState<TotalCountState>({
    my: { count: 0, limit: 0, offset: 0, total: 0, results: [] },
    open: { count: 0, limit: 0, offset: 0, total: 0, results: [] },
    completed: { count: 0, limit: 0, offset: 0, total: 0, results: [] },
  });
  const [dateRangeDetails, setDateRangeDetails] = useState<any>({});
  const [projects, setProjects] = useState<ProjectList>({
    count: 0,
    limit: 0,
    offset: 0,
    total: 0,
    results: [],
  });

  const { getMyProjectsDashboard, deleteProject, updateProjectStatus } = useProjectBuilder();
  const { locale } = useParams();
  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openProjectGuides = useCallback((e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    requestAnimationFrame(() => setIsModalOpen(true));
  }, []);

  useEffect(() => {
    isModalOpen && document.body.classList.add('root_overflow_hidden');
    !isModalOpen && document.body.classList.remove('root_overflow_hidden');
  }, [isModalOpen]);

  useEffect(() => {
    if (!isModalOpen) return;
    if (!locale) return;

    let cancelled = false;

    const loadProjectGuide = async () => {
      try {
        if (cancelled) return;
      } finally {
      }
    };

    loadProjectGuide();

    return () => {
      cancelled = true;
    };
  }, [isModalOpen, locale]);
  const fetchProjects = async () => {
    setLoading(true);
    try {
      const payload = { limit: 0, offset: 0 };
      const responseBody: any = await getMyProjectsDashboard(payload);

      setProjects(responseBody.data);
    } catch (err) {
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projects?.total === 0) {
      fetchProjects();
    }
  }, []);

  useEffect(() => {
    if (refetch) {
      setRefetch(false);
    }
  }, [refetch]);

  return (
    <div
      id="list-container"
    >
      <Dashboard
        title={'My Projects'}
        href="/project-builder"
      >
        <div className='min-h-[300px] pb-[18px]'>
          {loading && <>
            <Spinner />
            <div className="overlay fixed left-0 top-0 z-50 h-full w-full bg-gray-800 opacity-75"></div>
          </>}
          {!projects?.total && !loading &&
            <div className='py-8'>
              <span>
                <h1 className="fontSize32-sm font-medium">Let's start by creating your first project</h1>
              </span>
              <Link link={`/${locale}/project-builder/create`}>
                <Button
                  className="text-md w-full !cursor-pointer rounded-none !bg-gray-700 !px-[25px] !py-[13px] text-center disabled:cursor-auto disabled:!bg-gray-400 md:!mb-3 md:!mt-4 md:w-fit"
                  type="button"
                >
                  Create a Project
                </Button>
              </Link>
            </div>}
            {!!projects?.total &&
            <>
              <ProjectListMemo
                totalRecords={projects?.total}
                getMyProjectsDashboard={getMyProjectsDashboard}
                deleteProject={(src: any) => {
                  deleteProject(src);
                }}
                updateProjectStatus={(src: any) => {
                  updateProjectStatus(src);
                }}
                totalCount={totalCount}
                setTotalCount={setTotalCount}
                dateRangeDetails={dateRangeDetails}
                setDateRangeDetails={setDateRangeDetails}
              />
            </>
          }
        </div>
        <div className="mb-10">
                      <div className="flex gap-x-1 gap-y-2 sm:flex-row z-9999">
                        <Typography className="text-[15px] leading-normal font-normal !leading-6 text-[#090909]">
                          Questions? Check out the
                        </Typography>
                        <button
                          type="button"
                          onClick={openProjectGuides}
                          className="text-[15px] leading-normal font-normal cursor-pointer  !font-bold !leading-6 text-[#090909] underline"
                        >
                          FAQ Section
                        </button>
                      </div>
                    </div>
                    <div>

      </div>
      </Dashboard>
     <Modal
          className="mx-auto !w-[95vw] max-w-[1216px] rounded-lg bg-white shadow-xl"
                    isOpen={isModalOpen}
                    onRequestClose={closeModal}
                    preventScroll={false}
                    closeButton={true}
                    variant="default"
                    centered={true}
                    size="fit"
                    style={{ overlay: { zIndex: 9999 } }}
                  >
                    <div className="relative mx-auto !h-[90vh] overflow-y-auto !px-[1.125rem] sm:h-[80vh] sm:max-h-[43.75rem] sm:!px-10">
                      <ProjectBuilderGuide autoOpenAccordion={true} />
                    </div>
        </Modal>
    </div>
  );
}
